/**
 * 菜单状态管理
 */
import * as menuUtils from '../../../utils'
import { defineStore } from 'pinia'
import { ref, computed, watch } from 'vue'
import { useUserStore } from '../user'
import { useRoute } from 'vue-router'

export const useMenuStore = defineStore('menu', () => {
  // ============ 状态 ============
  const menus = ref<MenuItem[]>([])
  const flatMenus = ref<FlatMenuItem[]>([])
  const activeMenu = ref<string>('')
  const collapsed = ref<boolean>(false)
  const openedMenus = ref<string[]>([])  // 已展开的菜单
  const loading = ref<boolean>(false)
  const error = ref<string | null>(null)
  const initialized = ref<boolean>(false)

  // ============ 新增：横竖屏适配相关状态 ============
  const userCollapsedPreference = ref<boolean | null>(null)  // 记录用户手动设置的折叠状态
  const isLandscape = ref<boolean>(false)  // 是否横屏
  const isMobile = ref<boolean>(false)     // 是否移动端

  // ============ 依赖 ============
  const userStore = useUserStore()
  const route = useRoute()

  // ============ 计算属性 ============
  const sidebarMenus = computed(() => {
    if (!menus.value.length) return []
    try {
      const authorized = menuUtils.filterAuthorizedMenus(menus.value, userStore.role.split(','))
      const filtered = menuUtils.filterHiddenMenus(authorized)
      return menuUtils.sortMenus(filtered)
    } catch (err) {
      console.log(err)
      return []
    }
  })

  //判断是否是空菜单
  const isEmpty = computed(() => !sidebarMenus.value.length)
  //判断菜单是否折叠
  const isCollapsed = computed(() => collapsed.value)

  // ============ 新增：检测屏幕方向和尺寸 ============
  const checkScreenOrientation = () => {
    if (typeof window === 'undefined') return
    // 检测横竖屏
    isLandscape.value = window.matchMedia('(orientation: landscape)').matches
    // 检测是否为移动端（宽度 <= 768px）
    isMobile.value = window.innerWidth <= 768
    // 移动端横屏时自动折叠菜单
    if (isMobile.value && isLandscape.value) {
      // 如果是横屏，且菜单是展开状态，则自动折叠
      if (!collapsed.value) {
        userCollapsedPreference.value = false  // 记录用户原来是想展开的
        collapsed.value = true
        openedMenus.value = []
      }
    }
    // 移动端竖屏时，如果之前是因为横屏自动折叠的，且用户没有手动操作，则恢复
    else if (isMobile.value && !isLandscape.value) {
      // 如果之前是自动折叠的（用户偏好记录为 false），现在竖屏了，恢复展开
      if (userCollapsedPreference.value === false && collapsed.value) {
        collapsed.value = false
        userCollapsedPreference.value = null
        // 恢复展开当前激活菜单的父级
        if (activeMenu.value && activeMenu.value !== 'dashboard') {
          expandMenus(activeMenu.value)
        }
      }
    }
    // PC端或平板：不做自动调整，保持用户手动设置
  }

  // ============ 新增：监听窗口大小变化（防抖） ============
  let resizeTimer: ReturnType<typeof setTimeout> | null = null
  const handleResize = () => {
    if (resizeTimer) clearTimeout(resizeTimer)
    resizeTimer = setTimeout(() => {
      checkScreenOrientation()
    }, 150)
  }

  // ============ 新增：监听路由变化时重新检查屏幕方向（可选） ============
  const initOrientationListener = () => {
    if (typeof window === 'undefined') return
    // 初始检查
    checkScreenOrientation()
    // 监听窗口大小变化
    window.addEventListener('resize', handleResize)
  }

  /**
   * 菜单初始化
   */
  const initializeMenuData = async (): Promise<boolean> => {
    if (initialized.value && menus.value.length > 0) return true
    try {
      loading.value = true
      const allMenus = menuUtils.getMenusByRole(userStore.role)
      if (!Array.isArray(allMenus)) throw new Error('菜单数据格式错误')
      menus.value = allMenus
      flatMenus.value = menuUtils.flattenMenus(allMenus)
      initialized.value = true
      // 初始化完成后，启动横竖屏监听
      initOrientationListener()
      return true
    } catch (err) {
      error.value = err instanceof Error ? err.message : '菜单初始化失败'
      return false
    } finally {
      loading.value = false
    }
  }

  /**
   * 计算面包屑
   */
  const breadcrumb = computed(() => {
    if (!activeMenu.value || !sidebarMenus.value.length) return []
    // 处理 dashboard 页面
    if (activeMenu.value === 'dashboard') {
      return [{
        key: 'dashboard',
        path: '/layout/dashboard',
        title: '首页',
        icon: 'Dashboard',
        isHome: true,
      }]
    }
    const rawBreadcrumb = menuUtils.getBreadcrumbItems(flatMenus.value, activeMenu.value)
    if (rawBreadcrumb.length === 0) return []
    const seen = new Set<string>()
    const uniqueBreadcrumb: BreadcrumbItem[] = []
    for (const item of rawBreadcrumb) {
      const itemKey = item.key || `${item.path}-${item.title}`
      if (!seen.has(itemKey)) {
        seen.add(itemKey)
        uniqueBreadcrumb.push({
          ...item,
          key: itemKey,
          isHome: item.path === '/layout/dashboard' || item.title === '首页'
        })
      }
    }
    return uniqueBreadcrumb.slice(-2)
  })

  /**
   * 设置激活菜单
   * @param path 菜单路径
   */
  const setActiveMenu = (path: string) => {
    //如果路径不合法或者是首页则不更新
    if (!path) return
    // 修改：处理 dashboard 页面，设置特殊的激活状态而不是清空
    if(path.toLowerCase().includes('dashboard')){
      openedMenus.value = []
      activeMenu.value = 'dashboard'  // 设置为 'dashboard' 而不是空字符串
      return;
    }
    const normalizedPath = path.replace(/\/+/g, '/')
    // 避免重复设置相同的激活菜单
    if (activeMenu.value === normalizedPath) return
    activeMenu.value = normalizedPath
    expandMenus(normalizedPath)
  }

  /**
   * 切换折叠状态
   */
  const toggleCollapse = () => {
    collapsed.value = !collapsed.value
    // 记录用户手动设置的偏好
    userCollapsedPreference.value = collapsed.value
    if (collapsed.value) openedMenus.value = []
    // 展开时重新展开当前激活菜单的父级（dashboard 除外）
    else if (activeMenu.value && activeMenu.value !== 'dashboard') expandMenus(activeMenu.value)
  }

  /**
   * 根据当前路径更新需要展开的父菜单
   * @param path 当前路径
   */
  const expandMenus = (path: string) => {
    // 如果菜单折叠，直接返回
    if (collapsed.value) {
      openedMenus.value = []
      return
    }
    // 如果没有路径或菜单数据，直接返回
    if (!path || !sidebarMenus.value.length || !flatMenus.value.length) {
      openedMenus.value = []
      return
    }
    // dashboard 不需要展开菜单
    if (path.toLowerCase().includes('dashboard')) {
      openedMenus.value = []
      return
    }
    try {
      // 规范化路径，确保格式一致
      const normalizedPath = path.replace(/^\/+|\/+$/g, '')
      // 构建带 /layout 前缀的完整路径
      const fullPath = `/layout/${normalizedPath}`
      // 查找当前路径对应的菜单项
      const currentMenuItem = flatMenus.value.find(item => {
        const itemPath = item.fullPath || item.path
        // 确保比较时使用带 /layout 前缀的路径
        const normalizedItemPath = itemPath.startsWith('/layout')
          ? itemPath
          : `/layout/${itemPath.replace(/^\/+/, '')}`
        return normalizedItemPath === fullPath
      })
      if (!currentMenuItem) return
      // 获取面包屑（包含所有父级）
      const breadcrumbItems = menuUtils.getBreadcrumbItems(flatMenus.value, currentMenuItem.fullPath || currentMenuItem.path)
      if (breadcrumbItems.length === 0) return
      // 清空当前的展开菜单
      const newOpenedMenus: string[] = []
      // 排除最后一项（当前菜单），只展开父级菜单
      const parentMenus = breadcrumbItems.slice(0, -1)
      // 展开所有父级菜单
      parentMenus.forEach(menu => {
        // 构建带 /layout 前缀的完整路径
        const menuPath = menu.fullPath || menu.path
        const fullMenuPath = menuPath.startsWith('/layout')
          ? menuPath
          : `/layout/${menuPath.replace(/^\/+/, '')}`
        if (fullMenuPath && !newOpenedMenus.includes(fullMenuPath)) {
          newOpenedMenus.push(fullMenuPath)
        }
      })
      openedMenus.value = newOpenedMenus
    } catch (error) {
      console.error('菜单展开错误:', error)
    }
  }

  //监听路由更新
  watch(
    () => route.path,
    (newPath) => {
      if (newPath && initialized.value) {
        const normalizedPath = newPath.replace(/^\/layout\//, '').replace(/^\/+|\/+$/g, '')
        const menuPath = normalizedPath || 'dashboard'
        const newActiveMenu = menuPath === 'dashboard' ? 'dashboard' : menuPath
        if (activeMenu.value !== newActiveMenu) {
          setActiveMenu(newActiveMenu)
        }
      }
    },
    { immediate: true }
  )

  // ============ 新增：清理监听器（可选，用于组件卸载时） ============
  const cleanup = () => {
    if (typeof window !== 'undefined') window.removeEventListener('resize', handleResize)
    if (resizeTimer) clearTimeout(resizeTimer)
  }

  return {
    //属性
    menus,
    breadcrumb,
    flatMenus,
    initialized,
    sidebarMenus,
    activeMenu,
    collapsed,
    openedMenus,
    loading,
    error,
    isEmpty,
    isCollapsed,
    // 横竖屏相关状态（供组件使用）
    isLandscape,
    isMobile,
    //方法
    initializeMenuData,
    setActiveMenu,
    expandMenus,
    toggleCollapse,
    cleanup  // 在应用卸载时调用
  }
})
